import { useState } from "react";
import { EXPENSE_GROUPS } from "@dentacore/shared-types";
import type { ExpenseCategory, ExpenseGroup } from "@dentacore/shared-types";

import { Modal } from "../ui/modal";
import { EXPENSE_GROUP_LABELS } from "./format";
import styles from "./accounting.module.css";

/** One line of the list being edited. `id` is null for a category not yet saved. */
interface Draft {
  key: string;
  id: string | null;
  name: string;
  /** The saved name, so only a real rename is sent. */
  saved: string | null;
  group: ExpenseGroup;
  /** The saved group, so only a real move is sent. */
  savedGroup: ExpenseGroup | null;
  expenseCount: number;
}

/** The writes the dialog asks for, in the order they are sent. */
export interface CategoryChanges {
  remove: readonly ExpenseCategory[];
  /** A rename, a move to another group, or both — the whole row is sent. */
  rename: readonly { id: string; name: string; group: ExpenseGroup }[];
  add: readonly { name: string; group: ExpenseGroup }[];
}

/**
 * *Categories* — the clinic's own list of expense categories (AC-D4), never an
 * enum. Rename one and every expense in it follows, because an expense holds
 * the category's id and not its name. **Each sits in a group** — supplies and
 * lab, occupancy or other — which is how Accounting splits what the clinic
 * spends (AF-D5); a new one starts in *Other* and nothing is guessed.
 *
 * **A category with expenses in it cannot be removed** — it would leave those
 * expenses filed under nothing — so its Remove is disabled and says why; the
 * server refuses the same. Names are required and unique ignoring case, which
 * is checked here before anything is sent so a half-applied save is unlikely.
 */
export function CategoryDialog({
  categories,
  busy,
  onSave,
  onClose,
}: {
  categories: readonly ExpenseCategory[];
  busy: boolean;
  /**
   * Resolves true once every change landed. A failure part-way leaves the
   * dialog open; the page re-reads the list, and this dialog is reopened from
   * it, so nothing already saved is sent twice.
   */
  onSave: (changes: CategoryChanges) => Promise<boolean>;
  onClose: () => void;
}): React.JSX.Element {
  const [drafts, setDrafts] = useState<Draft[]>(() =>
    categories.map((category) => ({
      key: category.id,
      id: category.id,
      name: category.name,
      saved: category.name,
      group: category.group,
      savedGroup: category.group,
      expenseCount: category.expenseCount,
    })),
  );
  const [next, setNext] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const edit = (key: string, change: Partial<Pick<Draft, "name" | "group">>): void => {
    setError(null);
    setDrafts((was) => was.map((draft) => (draft.key === key ? { ...draft, ...change } : draft)));
  };

  const save = (): void => {
    const names = drafts.map((draft) => draft.name.trim());
    if (names.some((name) => name === "")) {
      setError("Every category needs a name.");
      return;
    }
    const folded = names.map((name) => name.toLocaleLowerCase());
    if (new Set(folded).size !== folded.length) {
      setError("Two categories have the same name.");
      return;
    }

    const kept = new Set(drafts.flatMap((draft) => (draft.id === null ? [] : [draft.id])));
    const changes: CategoryChanges = {
      remove: categories.filter((category) => !kept.has(category.id)),
      rename: drafts.flatMap((draft) =>
        draft.id !== null && (draft.name.trim() !== draft.saved || draft.group !== draft.savedGroup)
          ? [{ id: draft.id, name: draft.name.trim(), group: draft.group }]
          : [],
      ),
      add: drafts.flatMap((draft) =>
        draft.id === null ? [{ name: draft.name.trim(), group: draft.group }] : [],
      ),
    };
    if (changes.remove.length + changes.rename.length + changes.add.length === 0) {
      onClose();
      return;
    }
    void onSave(changes).then((landed) => {
      if (landed) onClose();
    });
  };

  return (
    <Modal label="Expense categories" onClose={onClose} testId="category-dialog">
      <form
        className={styles.dialogForm}
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        <h2 className={styles.dialogTitle}>Expense categories</h2>
        <p className={styles.dialogSub}>
          Your clinic&rsquo;s own list. Rename one and every expense in it follows. The group says
          where its expenses count in Accounting&rsquo;s overhead.
        </p>

        <div className={styles.categories}>
          {drafts.length === 0 && <p className={styles.note}>No categories yet.</p>}
          {drafts.map((draft) => (
            <div key={draft.key} className={styles.categoryRow} data-testid="category-row">
              <input
                className={styles.input}
                value={draft.name}
                aria-label="Category name"
                onChange={(event) => edit(draft.key, { name: event.target.value })}
                data-testid="category-name"
              />
              <select
                className={styles.select}
                value={draft.group}
                aria-label="Group"
                onChange={(event) => edit(draft.key, { group: event.target.value as ExpenseGroup })}
                data-testid="category-group"
              >
                {EXPENSE_GROUPS.map((group) => (
                  <option key={group} value={group}>
                    {EXPENSE_GROUP_LABELS[group]}
                  </option>
                ))}
              </select>
              <span className={styles.categoryCount} title="Expenses filed under it">
                {draft.expenseCount > 0 ? draft.expenseCount : ""}
              </span>
              <button
                type="button"
                className={styles.quiet}
                disabled={draft.expenseCount > 0}
                title={draft.expenseCount > 0 ? "Used by expenses, so it stays" : undefined}
                onClick={() => {
                  setError(null);
                  setDrafts((was) => was.filter((row) => row.key !== draft.key));
                }}
                data-testid="category-remove"
              >
                Remove
              </button>
            </div>
          ))}
          <div>
            <button
              type="button"
              className={styles.ghost}
              onClick={() => {
                setError(null);
                setDrafts((was) => [
                  ...was,
                  {
                    key: `new-${String(next)}`,
                    id: null,
                    name: "",
                    saved: null,
                    group: "OTHER",
                    savedGroup: null,
                    expenseCount: 0,
                  },
                ]);
                setNext((n) => n + 1);
              }}
              data-testid="category-add"
            >
              Add a category
            </button>
          </div>
        </div>

        <p className={styles.note}>A category with expenses in it cannot be removed.</p>
        {error !== null && <p className={styles.error}>{error}</p>}

        <div className={styles.dialogActions}>
          <button type="button" className={styles.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={styles.primary}
            disabled={busy}
            data-testid="category-save"
          >
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}
